import { useMemo, useState } from 'react';
import { Button, Grid, Popover, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { find, isEmpty, map } from 'lodash';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';
import MultiPracticeSelectField from 'common/forms/MultiPracticeSelectField';
import TextField from 'common/forms/TextField';
import { casingFormat } from 'common/utils';
import { updateNonRoutableRequestsFilter } from 'store/thunks/workQueueThunks';

import {
  NON_ROUTABLE_REASON_OPTIONS,
  NON_ROUTABLE_SOURCE_OPTIONS,
  NON_ROUTABLE_STATUS_OPTIONS,
} from '../constants/nonRoutableRequests';
import { endOfDayIso, isoToDateInput, startOfDayIso } from '../utils/nonRoutableRequestDates';

/**
 * Filters for the Non-Routable Orders holding queue (POM-6223).
 *
 * EVERY MULTI-SELECT CLEARS TO `undefined`, NEVER TO `[]`. An empty array is not "no filter" — it
 * reads as "exclude everything", so a cleared Status box would serve zero rows while the screen
 * said nothing is parked. The API layer sanitises this a second time
 * (sanitizeNonRoutableRequestsFilter), because on this screen a filter that silently hides
 * prescriptions is the failure mode that matters.
 *
 * `referenceId` is an EXACT match on the partner's own eScript reference, stored byte-identically
 * at intake — not a prefix and not case-insensitive.
 */
const NonRoutableRequestsQueueFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState({});
  const [selectedPartners, setSelectedPartners] = useState([]);

  const filter = useSelector(({ workQueue }) => workQueue.nonRoutableRequests.filter);
  const loading = useSelector(({ workQueue }) => workQueue.nonRoutableRequests.loading);
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const partnerOptions = useMemo(
    () => map(partners, ({ partnerName }) => casingFormat(partnerName)),
    [partners]
  );

  const selectedPartnerIds = useMemo(
    () =>
      selectedPartners
        .map(
          (name) =>
            find(partners, (p) => casingFormat(p.partnerName).toLowerCase() === name.toLowerCase())
              ?.partnerUuid
        )
        .filter(Boolean),
    [selectedPartners, partners]
  );

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);

    setSelectedPartners(
      map(
        (partners ?? []).filter((p) => filter?.partnerIds?.includes(p.partnerUuid)),
        (p) => casingFormat(p.partnerName)
      )
    );

    setFormInitialValues({
      referenceId: filter?.referenceId ?? '',
      parkedAfter: isoToDateInput(filter?.parkedAfter),
      parkedBefore: isoToDateInput(filter?.parkedBefore),
      statuses: NON_ROUTABLE_STATUS_OPTIONS.filter((opt) => filter?.statuses?.includes(opt.value)),
      reasons: NON_ROUTABLE_REASON_OPTIONS.filter((opt) => filter?.reasons?.includes(opt.value)),
      sources: NON_ROUTABLE_SOURCE_OPTIONS.filter((opt) => filter?.sources?.includes(opt.value)),
    });
  };

  const handleClose = () => setAnchorEl(null);

  const popoverId = anchorEl ? 'WorkQueue-NonRoutableRequestsFilterButton-Popover' : undefined;

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    dispatch(
      updateNonRoutableRequestsFilter({
        filter: {
          referenceId: formData.referenceId?.trim() || undefined,
          parkedAfter: startOfDayIso(formData.parkedAfter),
          parkedBefore: endOfDayIso(formData.parkedBefore),
          // `undefined`, never `[]` — see the file docblock.
          statuses: !isEmpty(formData.statuses) ? formData.statuses.map((s) => s.value) : undefined,
          reasons: !isEmpty(formData.reasons) ? formData.reasons.map((r) => r.value) : undefined,
          sources: !isEmpty(formData.sources) ? formData.sources.map((r) => r.value) : undefined,
          partnerIds: !isEmpty(selectedPartnerIds) ? selectedPartnerIds : undefined,
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { maxHeight: '70vh', overflowY: 'auto' } } }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={formInitialValues}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Reference Id'
                      name='referenceId'
                      id='WorkQueue-NonRoutableRequestsFilterButton-referenceId-input'
                    />
                    <Typography variant='caption' color='textSecondary'>
                      Exact match on the partner reference.
                    </Typography>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Partner'
                      name='partnerNames'
                      id='WorkQueue-NonRoutableRequestsFilterButton-partnerIds-input'
                      multiple
                      sortOptions
                      defaultValue={selectedPartners}
                      options={partnerOptions}
                      value={selectedPartners}
                      onChange={(event, newValue) => setSelectedPartners(newValue)}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Parked on or after'
                      name='parkedAfter'
                      type='date'
                      id='WorkQueue-NonRoutableRequestsFilterButton-parkedAfter-input'
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Parked on or before'
                      name='parkedBefore'
                      type='date'
                      id='WorkQueue-NonRoutableRequestsFilterButton-parkedBefore-input'
                    />
                  </Grid>

                  {/* THE REASON FILTER IS BACK (POM-6279). It was removed while the domain had one
                      member (STATE_NOT_LICENSED) and could only narrow the queue to everything or
                      to nothing; it now has four. */}
                  <MultiPracticeSelectField
                    label='Reason'
                    name='reasons'
                    id='WorkQueue-NonRoutableRequestsFilterButton-reasons-dropdown'
                    options={NON_ROUTABLE_REASON_OPTIONS}
                  />

                  {/* POM-6279: who parked the row — API intake, a relay fax, or an ESP / POM order
                      a routing rule parked. Each offers different actions. */}
                  <MultiPracticeSelectField
                    label='Source'
                    name='sources'
                    id='WorkQueue-NonRoutableRequestsFilterButton-sources-dropdown'
                    options={NON_ROUTABLE_SOURCE_OPTIONS}
                  />

                  <MultiPracticeSelectField
                    label='Status'
                    name='statuses'
                    id='WorkQueue-NonRoutableRequestsFilterButton-statuses-dropdown'
                    options={NON_ROUTABLE_STATUS_OPTIONS}
                  />

                  <Grid container justifyContent='flex-end' sx={{ pt: 1 }}>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default NonRoutableRequestsQueueFilterButton;
